// All HTTP transport lives here — the single swap point for a future Outlook
// add-in build (auth header) or M365 re-host. Nothing else imports fetch.
import type { ChatResponse, Company, CuratedEntry, EmbeddingMap, Mode, Source } from './types';

const BASE = import.meta.env.VITE_API_BASE_URL || '/api';

// Interim shared access key (until Entra ID). Kept in localStorage and sent on
// every request; a 401 throws AccessError so the AccessGate can (re)prompt.
const ACCESS_KEY_STORAGE = 'nina_access_key';

export class AccessError extends Error {
  constructor() {
    super('Toegangscode vereist of onjuist.');
    this.name = 'AccessError';
  }
}

export function getAccessKey(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY_STORAGE);
  } catch {
    return null;
  }
}

export function setAccessKey(key: string): void {
  try {
    localStorage.setItem(ACCESS_KEY_STORAGE, key);
  } catch {
    /* private mode: key lives for this page load only via header below */
  }
}

function accessHeaders(): Record<string, string> {
  const key = getAccessKey();
  return key ? { 'x-access-key': key } : {};
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new AccessError();
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.error || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

export async function getCompanies(): Promise<Company[]> {
  const res = await fetch(`${BASE}/companies`, { headers: accessHeaders() });
  const data = await json<{ companies: Company[] }>(res);
  return data.companies;
}

export async function postChat(input: {
  conversationId: string;
  company: string;
  division: string;
  mode: Mode;
  message: string;
}): Promise<ChatResponse> {
  const res = await fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...accessHeaders() },
    body: JSON.stringify(input),
  });
  return json<ChatResponse>(res);
}

export async function postUpload(
  file: File,
  company: string,
  division: string
): Promise<{ fileId: string; chunks: number; filename: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('company', company);
  form.append('division', division);
  const res = await fetch(`${BASE}/upload`, {
    method: 'POST',
    headers: accessHeaders(),
    body: form,
  });
  return json(res);
}

export async function getEmbeddingMap(company = 'de-graaf'): Promise<EmbeddingMap> {
  const res = await fetch(`${BASE}/embeddings/map?company=${encodeURIComponent(company)}`, {
    headers: accessHeaders(),
  });
  return json<EmbeddingMap>(res);
}

// Who is adding knowledge. The access gate is a shared code, so the server
// cannot tell people apart — attribution has to come from the client until
// Entra SSO lands. Anonymous curated entries are exactly what makes staff
// knowledge unfixable a year later.
const AUTHOR_STORAGE = 'nina_author';

export function getAuthor(): string | null {
  try {
    return localStorage.getItem(AUTHOR_STORAGE);
  } catch {
    return null;
  }
}

export function setAuthor(name: string): void {
  try {
    localStorage.setItem(AUTHOR_STORAGE, name);
  } catch {
    /* private mode */
  }
}

export async function postKnowledge(input: {
  question: string;
  answer: string;
  author: string;
  company?: string;
  division?: string;
}): Promise<{ ref: string; chunks: number; createdBy: string }> {
  const res = await fetch(`${BASE}/knowledge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...accessHeaders() },
    body: JSON.stringify(input),
  });
  return json(res);
}

export async function getKnowledge(company = 'de-graaf'): Promise<CuratedEntry[]> {
  const res = await fetch(`${BASE}/knowledge?company=${encodeURIComponent(company)}`, {
    headers: accessHeaders(),
  });
  const data = await json<{ entries: CuratedEntry[] }>(res);
  return data.entries;
}

export async function deleteKnowledge(ref: string, company = 'de-graaf'): Promise<void> {
  const res = await fetch(
    `${BASE}/knowledge/${encodeURIComponent(ref)}?company=${encodeURIComponent(company)}`,
    { method: 'DELETE', headers: accessHeaders() }
  );
  await json(res);
}

// Streaming variant of postChat. Total time is the same; the point is that the
// sources land after ~1s and the text arrives as it is written, instead of the
// user watching nothing for six seconds.
//
// Hand-rolled SSE parsing rather than EventSource: EventSource is GET-only and
// cannot send the access-key header.
export async function postChatStream(
  input: {
    conversationId: string;
    company: string;
    division: string;
    mode: Mode;
    message: string;
  },
  handlers: {
    onMeta?: (meta: { category: string; confidence?: number; sources: Source[] }) => void;
    onDelta?: (text: string) => void;
  }
): Promise<string> {
  const res = await fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...accessHeaders() },
    body: JSON.stringify({ ...input, stream: true }),
  });

  if (res.status === 401) throw new AccessError();
  if (!res.ok || !res.body) {
    let detail = res.statusText;
    try {
      detail = (await res.json()).error || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line; a frame can straddle two chunks,
    // so whatever trails the last separator stays in the buffer.
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      const event = /^event:\s*(.+)$/m.exec(frame)?.[1]?.trim();
      const raw = /^data:\s*([\s\S]*)$/m.exec(frame)?.[1];
      if (!event || !raw) continue;
      const data = JSON.parse(raw);

      if (event === 'meta') handlers.onMeta?.(data);
      else if (event === 'delta') {
        answer += data.text;
        handlers.onDelta?.(data.text);
      } else if (event === 'error') throw new Error(data.error);
      else if (event === 'done') answer = data.answer ?? answer;
    }
  }

  return answer;
}
